import { Component, Host, h, Prop, Element, State, Watch, Listen } from '@stencil/core';
import { isMagnoliaEditingMode } from '../../helper';

interface IndexInfo {
  name: string;
  currentValue: number;
  lastDayClosePrice: number;
  chartData: [number, number][];
}

interface ApiAnswer {
  historyQuoteList: HistoryQuoteList;
}

interface HistoryQuoteList {
  quote: Quote[];
  snapQuote: SnapQuote;
  security: Security;
  meta: Meta;
}

interface Quote {
  quoteDate: string;
  openPrice?: number;
  closePrice?: number;
  lowPrice?: number;
  highPrice?: number;
  cumulativeVolumeUnit: any;
}

interface SnapQuote {
  exchange: Exchange;
  permId: number;
  quoteDate: string;
  openPrice: number;
  lastPrice: number;
  lowPrice: number;
  highPrice: number;
  bidPrice: number;
  askPrice: number;
  yesterdayPrice: number;
  cumulativeVolumeUnit: number;
}

interface Exchange {
  exchangeCode: string;
  exchangeCodeVwd: string;
  exchangeId: number;
}

interface Security {
  instrumentKey: string;
  symbol: string;
  isin: string;
  description: string;
  securityGroup: string;
  currencyName: string;
}

interface Meta {
  requestParam: RequestParam;
  elapsedTime: number;
  quoteCount: number;
  totalQuoteCount: number;
  startBuildDate: number;
  endBuildDate: number;
}

interface RequestParam {
  aggregation: string;
  dest: string;
  end: string;
  instrumentKey: string;
  processPayment: string;
  processSplit: string;
  quoteType: string;
  start: string;
}

interface UiCmsStockIndicesBody {
  headline: string;
  indices: string[];
  indicesApiUrl: string;
  apiToken: string;
}

interface UiCmsStockIndicesI18n {
  readMore: string;
  close: string;
  previousDayChange: string;
  time: string;
  clock?: string; // optional because of english time format (11:00 AM <-> 11:00 Uhr)
}

@Component({
  tag: 'ui-cms-stock-indices',
  styleUrls: { default: 'ui-cms-stock-indices.scss', editing: 'ui-cms-stock-indices.magnolia.scss' },
  shadow: false,
})
export class UiCmsStockIndices {
  @Element() el: HTMLElement;

  @State() open: boolean;

  @State() loading = false;

  @Prop({ mutable: true }) body: UiCmsStockIndicesBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @Prop({ mutable: true }) i18n: UiCmsStockIndicesI18n = {
    readMore: 'Mehr erfahren',
    close: 'Schließen',
    previousDayChange: 'Veränderung zum Vortag',
    time: 'Uhrzeit',
  };

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  @Watch('utilityI18n')
  utilityI18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
  }

  @State() activeIndex = 0;

  @State() config: any;

  @State() dataCache: IndexInfo[] = [];

  @State() apiBreakdown: boolean;

  @Watch('activeIndex')
  @Watch('dataCache')
  updateChartConfig() {
    if (this.selectedIndexInformation) {
      this.config = {
        ...this.config,
        series: [
          {
            name: 'Tagesschlusskurs Vortag',
            type: undefined,
            data: this.selectedIndexInformation.chartData?.map((d) => [
              d[0],
              this.selectedIndexInformation.lastDayClosePrice,
            ]),
            tooltip: {
              valueDecimals: 3,
            },
            color: '#c7d5e6',
            marker: {
              radius: 5,
              symbol: 'circle',
            },
          },
          {
            name: this.selectedIndexInformation.name,
            type: undefined,
            data: this.selectedIndexInformation.chartData,
            tooltip: {
              valueDecimals: 3,
            },
            color: '#00358e',
            marker: {
              radius: 5,
              symbol: 'circle',
            },
          },
        ],
      };
    }
  }

  private failedFetches = 0;

  get indices(): string[] {
    return this.body.indices || [];
  }

  get balanceStatusPositive(): boolean {
    return this.getBalanceStatusForIndex(this.selectedIndexInformation);
  }

  get selectedIndexInformation(): IndexInfo {
    return this.dataCache[this.activeIndex];
  }

  get defaultConfig() {
    // DEV NOTE: has to be deconstructed because of "this" context
    const { i18n, localizeTime } = this;
    return {
      xAxis: {
        type: 'datetime',
        labels: {
          formatter() {
            return localizeTime(this.value);
          },
        },
      },
      tooltip: {
        valueDecimals: 3,
        useHTML: true,
        padding: 15,
        backgroundColor: '#ffffff',
        formatter() {
          let toolTip = `<div class="chart-tooltip__header"><span>${i18n.time}:</span><span>${localizeTime(
            this.x
          )}</span></div>`;
          toolTip += this.points.reduce(
            (s, point) => `
            <div class="chart-tooltip__content">
              <div class="chart-tooltip__item">
                <div class="chart-tooltip__item-color" style="background:${point.series.color}"></div>
                ${point.series.name}:
              </div>
              <div class="chart-tooltip__amount">${parseFloat(point.y.toFixed(3))}</div>
            </div>${s}`,
            ''
          );
          return toolTip;
        },
        borderWidth: 0,
        shared: true,
        split: false,
      },
    };
  }

  private localizeTime(time): string {
    return new Date(time).toLocaleTimeString(document.documentElement.lang, {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getLastTimestampOfSelectedIndex(currentIndex: IndexInfo): Date {
    const chartData = currentIndex?.chartData;
    // get last item from chartData array
    const dateFromLastItem = chartData?.[chartData.length - 1][0];
    return new Date(dateFromLastItem);
  }

  toTwoDecimals(input: number): string {
    return input.toLocaleString(document.documentElement.lang, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  }

  dateTimeToMilliseconds(dateTime: string): number {
    const [time, date] = dateTime.split(' ');
    const [day, month, year] = time.split('.');
    return Date.parse(`${month}/${day}/${year} ${date}`);
  }

  async componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
    this.utilityI18nChangeHandler(this.utilityI18n);
    this.config = this.defaultConfig;

    this.loading = true;
    if (this.body.indices) {
      const apiAnwers: ApiAnswer[] = await Promise.all(
        this.body.indices?.map((stockIndex) => this.fetchIndexStatus(stockIndex))
      );

      // we have to check, if all the data we need is delivered in the API results.
      const anwersWithAllNeededData = apiAnwers.filter(
        (answer) =>
          answer !== undefined &&
          answer.historyQuoteList?.security?.description !== undefined &&
          answer.historyQuoteList?.quote !== undefined &&
          answer.historyQuoteList?.snapQuote?.yesterdayPrice !== undefined
      );

      const incompleteAnswers = apiAnwers.filter((answer) => !anwersWithAllNeededData.includes(answer));

      incompleteAnswers?.forEach((answer) => {
        const name = answer?.historyQuoteList?.security?.description || 'name missing';
        // eslint-disable-next-line no-console
        console.log(
          `${name} Börse item is missing one or more properties : name, quote (we can not get Close price) or snapQuote (we can not get yesterdayPrice)`
        );
      });

      this.dataCache = anwersWithAllNeededData?.map((answer) => ({
        name: answer.historyQuoteList.security.description,
        currentValue: answer.historyQuoteList.quote[answer.historyQuoteList.quote.length - 1].closePrice,
        lastDayClosePrice: answer.historyQuoteList.snapQuote.yesterdayPrice,
        chartData: answer.historyQuoteList.quote?.map((quote) => [
          this.dateTimeToMilliseconds(quote.quoteDate),
          quote.closePrice,
        ]),
      }));
    }

    this.loading = false;
  }

  onToggleClick() {
    this.open = !this.open;
  }

  @Listen('sd-change')
  onSdRadioChange(event: any) {
    const target = event.target as HTMLElement;
    if (!target || target.id !== 'stock-radio-group') {
      return;
    }

    const value = event.srcElement.value as number;

    this.changeStock(value);
  }

  async changeStock(index: number) {
    this.activeIndex = index;
  }

  async fetchIndexStatus(stockName: string): Promise<ApiAnswer> {
    const url = new URL(this.body.indicesApiUrl);
    const todayDate = new Date();
    const today = todayDate.getTime().toString();
    url.searchParams.append('aggregation', '15min');
    url.searchParams.append('instrumentKey', stockName);
    url.searchParams.append('start', today);
    url.searchParams.append('end', today);
    const stockData: ApiAnswer = await fetch(url.toString(), {
      headers: {
        'x-api-key': this.body.apiToken,
      },
    })
      .then((resp) => {
        if (resp.status >= 400 && resp.status < 600) {
          throw new Error('Bad response from server');
        }
        return resp.json();
      })
      .catch(() => {
        this.failedFetches += 1;
        this.apiBreakdown = this.failedFetches === this.indices.length;
      });

    return stockData;
  }

  getPercentageForIndex(indexInfo: IndexInfo): number {
    if (!indexInfo) {
      return 0;
    }
    const percDiff = (a, b) => ((a - b) / ((a + b) / 2)) * 100;

    return percDiff(indexInfo.currentValue, indexInfo.lastDayClosePrice);
  }

  getBalanceStatusForIndex(indexInfo: IndexInfo) {
    return this.getPercentageForIndex(indexInfo) > 0;
  }

  private renderContent() {
    if (!this.open || this.apiBreakdown) return null;

    const lastTimestampOfSelectedIndex = this.getLastTimestampOfSelectedIndex(this.selectedIndexInformation);

    return (
      <div class="stock-indices-wrapper__content">
        <div class="stock-indices-wrapper__header">
          <h1 class="sd-headline sd-headline--size-3xl">{this.body.headline}</h1>
        </div>
        <div class="stock-indices-wrapper__actions">
          <sd-radio-group id="stock-radio-group" size="sm" value={0}>
            {this.dataCache?.map((stock, i) => <sd-radio-button value={i}>{stock.name}</sd-radio-button>)}
          </sd-radio-group>
          <sd-button variant="tertiary" onClick={this.onToggleClick.bind(this)}>
            {this.i18n.close}
            <sd-icon
              slot="icon-right"
              library={window.ui.cmsState.iconLibrary}
              name="union-investment/system/chevron-top"
            ></sd-icon>
          </sd-button>
        </div>
        {this.selectedIndexInformation && (
          <div class="content-container">
            <div class="content-container__column-info">
              <p class="sd-paragraph stock-title">
                {this.selectedIndexInformation.name}
                <span class="stock-price-mobile">{this.toTwoDecimals(this.selectedIndexInformation.currentValue)}</span>
              </p>
              <p class="sd-paragraph stock-price">{this.toTwoDecimals(this.selectedIndexInformation.currentValue)}</p>
              <p class="sd-paragraph stock-last-day">
                {this.i18n.previousDayChange}
                <span class={`stock-balance ${this.balanceStatusPositive ? 'positive' : 'negative'}`}>
                  {this.toTwoDecimals(this.getPercentageForIndex(this.selectedIndexInformation))}%
                </span>
              </p>
              <p class="sd-paragraph stock-date">{`${lastTimestampOfSelectedIndex.toLocaleDateString(
                document.documentElement.lang,
                {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                }
              )} | ${this.localizeTime(lastTimestampOfSelectedIndex)} ${this.i18n.clock ?? ''}`}</p>
            </div>
            <div class="content-container__column-chart">
              <ui-cms-chart config={this.config} />
            </div>
          </div>
        )}
        {this.loading && (
          <div class="overlay">
            <sd-spinner color="primary"></sd-spinner>
          </div>
        )}
      </div>
    );
  }

  private renderPreview() {
    if (this.open || this.apiBreakdown) return null;

    return (
      <div class="stock-indices-wrapper__header">
        <div class="stock-indices-wrapper__header-preview">
          {this.dataCache.map((indexInfo) => (
            <p class="stock-indices-wrapper__header-item">
              <span>{indexInfo.name}</span>
              <span>
                <span class="header-item-amount">{this.toTwoDecimals(indexInfo.currentValue)}</span>
                <span class={`stock-balance ${this.getBalanceStatusForIndex(indexInfo) ? 'positive' : 'negative'}`}>
                  {this.toTwoDecimals(this.getPercentageForIndex(indexInfo))}%
                </span>
              </span>
            </p>
          ))}
        </div>
        <sd-button variant="tertiary" onClick={this.onToggleClick.bind(this)}>
          {this.i18n.readMore}
          <sd-icon
            slot="icon-right"
            library={window.ui.cmsState.iconLibrary}
            name="union-investment/system/chevron-bottom"
          ></sd-icon>
        </sd-button>
      </div>
    );
  }

  render() {
    return (
      <Host>
        <section class="stock-indices-wrapper">
          {this.apiBreakdown && isMagnoliaEditingMode() && <sd-spinner color="primary"></sd-spinner>}
          {this.renderPreview()}
          {this.renderContent()}
        </section>
      </Host>
    );
  }
}
